import type { ReactNode } from 'react';
import { Navigate, createBrowserRouter, useLocation } from 'react-router';
import { AppLayout } from '@/ui/Layout.js';
import { Spinner } from '@/ui/primitives.js';
import { onboardingGap, useAuth } from '@/stores/auth.js';
import { LoginPage } from '@/features/auth/LoginPage.js';
import { ConsentPage } from '@/features/auth/ConsentPage.js';
import { OnboardingPage } from '@/features/onboarding/OnboardingPage.js';
import { GuidePage } from '@/features/onboarding/GuidePage.js';
import { SourcesPage } from '@/features/sources/SourcesPage.js';
import { AdminPage } from '@/features/admin/AdminPage.js';
import { PostureCheckPage } from '@/features/check/PostureCheckPage.js';
import { CaptureEntry } from '@/features/capture/PhoneGuideIntro.js';
import { LiveSessionPage } from '@/features/session/LiveSessionPage.js';
import { ReportPage } from '@/features/report/ReportPage.js';
import { ProgressPage } from '@/features/progress/ProgressPage.js';
import { CoachPage } from '@/features/coach/CoachPage.js';
import { ExercisesPage } from '@/features/exercises/ExercisesPage.js';
import { SettingsPage } from '@/features/settings/SettingsPage.js';
import { DataOptInPage } from '@/features/data/DataOptInPage.js';
import { LegalPage, LEGAL_DOCS } from '@/features/legal/LegalPage.js';
import { NotFoundPage } from '@/features/legal/NotFoundPage.js';
import { LandingPage } from '@/features/landing/LandingPage.js';
import { ProgramPage } from '@/features/program/ProgramPage.js';

/**
 * react-router 8 in data mode. Route guards are components rather than loaders
 * because the gate depends on the auth listener, which is a subscription.
 */
function RequireAuth({ children }: { children: ReactNode }): ReactNode {
  const { user, userDoc, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="grid min-h-60 place-items-center">
        <Spinner label="Checking your session" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  const gap = onboardingGap(userDoc);
  if (gap === 'consent' && location.pathname !== '/consent') {
    return <Navigate to="/consent" replace />;
  }
  if (gap === 'profile' && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

const debugRoutes = import.meta.env.DEV
  ? [
      {
        path: 'debug',
        lazy: async () => {
          const { DebugPage } = await import('@/features/debug/DebugPage.js');
          return { Component: DebugPage };
        },
      },
    ]
  : [];

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  // Public web landing page; never the root (the app is), see LandingPage.
  { path: '/landing', element: <LandingPage /> },
  ...LEGAL_DOCS.map((docPage) => ({
    path: `/legal/${docPage.slug}`,
    element: <LegalPage slug={docPage.slug} />,
  })),
  // Team dashboard: outside the phone shell (it scrolls like a web page and
  // spreads out on a desktop). Reached only by URL; AdminPage checks the email.
  {
    path: '/admin',
    element: (
      <RequireAuth>
        <AdminPage />
      </RequireAuth>
    ),
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      // Home is Progress, not the camera: opening the app should not grab the
      // camera before the user has chosen to record anything.
      { index: true, element: <Navigate to="/progress" replace /> },
      { path: 'consent', element: <ConsentPage /> },
      { path: 'onboarding', element: <OnboardingPage /> },
      { path: 'guide', element: <GuidePage /> },
      { path: 'sources', element: <SourcesPage /> },
      { path: 'capture', element: <CaptureEntry /> },
      { path: 'check', element: <PostureCheckPage /> },
      { path: 'session', element: <LiveSessionPage /> },
      { path: 'report/:sessionId', element: <ReportPage /> },
      { path: 'progress', element: <ProgressPage /> },
      { path: 'coach', element: <CoachPage /> },
      { path: 'program', element: <ProgramPage /> },
      { path: 'exercises', element: <ExercisesPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'settings/data', element: <DataOptInPage /> },
      ...debugRoutes,
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
